import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { EnginePanel, ActivityFeed } from "@/components/LiveEngine";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Activity, Crosshair, ShieldCheck, Radar } from "lucide-react";
import { toast } from "sonner";
import { usePoll } from "@/lib/usePoll";
import { api, usd, signed, num, ago, errMsg } from "@/lib/api";
import { Panel, Metric, Badge, Empty, PageTitle } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function PriceChart() {
  const [iv, setIv] = useState(60);
  const { data } = usePoll(`/market/ohlc?interval=${iv}`, 60000, [iv]);
  const rows = (data || []).slice(-150).map((r) => ({ t: new Date(r.time * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit" }), c: r.close }));
  return (
    <Panel title="BTC/USD · Kraken" icon={Activity} testid="price-chart" className="lg:col-span-3"
      right={<div className="flex gap-1">{[[15, "15m"], [60, "1h"], [240, "4h"], [1440, "1d"]].map(([v, l]) => (
        <button key={v} data-testid={`chart-interval-${l}`} onClick={() => setIv(v)} className={`font-mono text-[10px] px-2 py-1 rounded ${iv === v ? "bg-[#F7931A] text-black" : "text-zinc-400 hover:bg-zinc-800"}`}>{l}</button>))}</div>}>
      <div className="h-64">
        <ResponsiveContainer>
          <AreaChart data={rows}>
            <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F7931A" stopOpacity={0.35} /><stop offset="100%" stopColor="#F7931A" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="t" tick={{ fill: "#71717a", fontSize: 10 }} minTickGap={40} />
            <YAxis domain={["auto", "auto"]} tick={{ fill: "#71717a", fontSize: 10 }} width={60} orientation="right" />
            <Tooltip contentStyle={{ background: "#12141D", border: "1px solid #27272a", fontFamily: "JetBrains Mono", fontSize: 12 }} />
            <Area type="monotone" dataKey="c" stroke="#F7931A" strokeWidth={1.8} fill="url(#g)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

function Decision({ d, onRun, running }) {
  const fd = d?.final_decision || "NO_TRADE";
  const color = fd === "BUY" ? "text-emerald-400" : fd === "SHORT" ? "text-red-400" : "text-amber-400";
  return (
    <Panel title="AI Trading Decision" icon={Crosshair} testid="ai-decision-card" right={<Button data-testid="run-analysis-button" size="sm" variant="secondary" disabled={running} onClick={onRun} className="font-mono text-[10px] uppercase">{running ? "Analysing…" : "Run analysis"}</Button>}>
      <div data-testid="ai-final-decision" className={`font-display text-4xl font-extrabold tracking-tight ${color}`}>{fd === "NO_TRADE" ? "NO TRADE" : fd}</div>
      <div className="font-mono text-xs text-zinc-500 mt-2">
        {d ? <>Chief: {d.chief_decision?.replace("_", " ")} · p={d.probability ?? "—"} · risk {d.risk_level || "—"} · {ago(d.ts)}</> : "No analysis has run yet."}
      </div>
      {d?.reasoning && <p className="text-sm text-zinc-400 mt-3 line-clamp-4">{d.reasoning}</p>}
      {d?.gate?.blocks?.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="decision-blocks">
          {d.gate.blocks.slice(0, 4).map((b, i) => <li key={i} className="font-mono text-[11px] text-amber-400/90">▸ {b}</li>)}
        </ul>
      )}
      <Link to="/agents" className="inline-block mt-4 font-mono text-[11px] text-[#F7931A] hover:underline" data-testid="view-agents-link">Open AI Command Center →</Link>
    </Panel>
  );
}

function PositionCard({ p, price, reload }) {
  const [pw, setPw] = useState("");
  const close = async () => {
    try {
      await api.post(`/positions/${p.id}/close`, { password: pw });
      toast.success("Position closed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!p) return <Empty testid="no-open-positions">No open positions. The bot is waiting for an exceptional opportunity.</Empty>;
  return (
    <div data-testid="open-position" className="space-y-2 font-mono text-xs">
      <div className="flex items-center gap-2"><Badge v={p.side === "long" ? "BUY" : "SHORT"}>{p.side} · {p.product}</Badge><span className="text-zinc-500">{p.status}</span></div>
      <div className="grid grid-cols-2 gap-2 text-zinc-400">
        <span>Entry <b className="text-zinc-100">{num(p.entry_price, 1)}</b></span><span>Size <b className="text-zinc-100">{p.volume} BTC</b></span>
        <span>Stop <b className="text-red-400">{num(p.stop_price, 1)}</b></span><span>Target <b className="text-emerald-400">{num(p.tp_price, 1)}</b></span>
        <span>Leverage <b className="text-zinc-100">{num(p.leverage, 2)}x</b></span><span>Liq. est <b className="text-zinc-100">{p.liquidation_price_est ? num(p.liquidation_price_est, 0) : "n/a"}</b></span>
      </div>
      <div className="flex gap-2 pt-2">
        <Input data-testid="close-position-password" type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} className="h-8 text-xs" />
        <Button data-testid="close-position-button" size="sm" variant="destructive" onClick={close} disabled={!pw}>Close</Button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { status } = useOutletContext() || {};
  const { data: d, reload } = usePoll("/dashboard", 5000);
  const { data: hist } = usePoll("/challenge", 30000);
  const [running, setRunning] = useState(false);
  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/agents/run");
      toast(r.data.started ? "Analysis cycle started — 8 agents working (1-3 min)" : r.data.reason);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setTimeout(() => setRunning(false), 5000);
  };
  const c = d?.connection;
  const dir = d?.last_decision?.gate ? (d.last_decision.final_decision !== "NO_TRADE" ? d.last_decision.final_decision : "NO_TRADE") : null;
  const curve = (hist?.history || []).map((h) => ({ t: new Date(h.ts).toLocaleDateString(), total: h.total, active: h.active, reserve: h.reserve }));
  return (
    <div>
      <PageTitle title="Command Dashboard" sub="Live Kraken data. Every number below is either verified from the exchange, from the bot's own ledger, or clearly marked unavailable." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Metric testid="metric-btc-price" label="Live BTC price" value={d?.price ? `$${num(d.price, 1)}` : "—"} tone="gold" sub={c?.websocket?.healthy ? "Kraken WebSocket · live" : "Feed unavailable"} />
        <Metric testid="metric-active-capital" label="Active trading capital" value={usd(d?.active_capital, 4)} sub="Only this is used for sizing" />
        <Metric testid="metric-reserve" label="Permanent 10% reserve" value={usd(d?.reserve, 4)} tone="blue" sub={d?.reserve_unsegregated > 0 ? "Ledger only · not physically segregated" : d?.reserve > 0 ? "Owner-confirmed segregated" : "Funded by 10% of net profits"} />
        <Metric testid="metric-total-portfolio" label="Total portfolio value" value={usd(d?.total_portfolio, 4)} sub="Active + reserve" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric testid="metric-today-pnl" label="Today's realized P/L" value={signed(d?.today_realized, 4)} tone={d?.today_realized > 0 ? "up" : d?.today_realized < 0 ? "down" : null} sub={`${d?.today_trades ?? 0} trades · daily limit ${usd(d?.daily_loss_limit)}`} />
        <Metric testid="metric-total-pnl" label="Total realized P/L" value={signed(d?.total_realized, 4)} tone={d?.total_realized > 0 ? "up" : d?.total_realized < 0 ? "down" : null} sub={`Fees ${usd(d?.fees_total, 4)} · funding ${usd(d?.funding_total, 4)}`} />
        <Metric testid="metric-unrealized" label="Unrealized P/L" value={signed(d?.unrealized, 4)} tone={d?.unrealized > 0 ? "up" : d?.unrealized < 0 ? "down" : null} sub="Open position mark" />
        <Metric testid="metric-risk-level" label="Risk level · market direction" value={(d?.last_decision?.risk_level || "—").toUpperCase()} sub={`Direction: ${dir === "BUY" ? "LONG" : dir === "SHORT" ? "SHORT" : d?.last_decision ? "No edge — standby" : "awaiting analysis"} · losses in a row ${d?.consecutive_losses ?? 0}`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <PriceChart />
        <Decision d={d?.last_decision} onRun={run} running={running} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <EnginePanel status={status} />
        <ActivityFeed />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Open positions" icon={Radar} testid="positions-panel"><PositionCard p={d?.position} price={d?.price} reload={reload} /></Panel>
        <Panel title="Connection health" icon={ShieldCheck} testid="connection-panel">
          <div className="space-y-2 font-mono text-xs">
            {[["Kraken WebSocket", c?.websocket?.healthy, c?.websocket?.error], ["Kraken Spot private API", c?.spot_private?.configured ? c?.spot_private?.ok !== false : false, c?.spot_private?.configured ? c?.spot_private?.error : "API keys not configured"],
              ["Kraken Futures private API", c?.futures_private?.configured ? c?.futures_private?.ok !== false : false, c?.futures_private?.configured ? c?.futures_private?.error : "API keys not configured"],
              ["Whale Alert stream", c?.whale_alert?.connected, c?.whale_alert?.error], ["Blockchain mempool feed", c?.blockchain_feed?.ok, c?.blockchain_feed?.error]].map(([n, ok, e]) => (
              <div key={n} className="flex items-start justify-between gap-3 border-b border-zinc-800/60 pb-2">
                <span className="text-zinc-300">{n}</span>
                <span className={ok ? "text-emerald-400" : "text-red-400"}>{ok ? "OK" : e ? String(e).slice(0, 48) : "DOWN"}</span>
              </div>
            ))}
            <div className={`pt-1 ${c?.entries_allowed && !d?.locked ? "text-emerald-400" : "text-red-400"}`} data-testid="dashboard-entries-status">New entries: {d?.locked ? "LOCKED" : c?.entries_allowed ? "allowed (if all gates pass)" : "BLOCKED"}</div>
          </div>
        </Panel>
        <Panel title="Portfolio growth" testid="equity-mini-chart">
          {curve.length < 2 ? <Empty>Growth history appears after trades and hourly snapshots.</Empty> : (
            <div className="h-44"><ResponsiveContainer><AreaChart data={curve}><XAxis dataKey="t" hide /><YAxis hide domain={["auto", "auto"]} /><Tooltip contentStyle={{ background: "#12141D", border: "1px solid #27272a", fontSize: 12 }} />
              <Area dataKey="total" stroke="#22C55E" fill="#22C55E22" isAnimationActive={false} /><Area dataKey="reserve" stroke="#3B82F6" fill="#3B82F622" isAnimationActive={false} /></AreaChart></ResponsiveContainer></div>)}
        </Panel>
      </div>
    </div>
  );
}
